import { useHttpClient } from '@airship-platform/airship-patterns';
import { useCallback } from 'react';
import { type RxCollection } from 'rxdb';
import { bufferTime, filter, map } from 'rxjs/operators';
import { useOfflineProviderInit } from '../useOfflineProviderInit';
import { useOfflineDbData } from './OfflineDbProvider';
import { setupEntityPush } from './setupEntityPush';
import { type CollectionName, type DocOf } from './types';

export const useEntityReplication = <K extends CollectionName>(
  collectionName: K,
  replicationIdentifier: string,
  callbacks?: {
    onSync?: () => Promise<void>;
    upsert?: (items: DocOf<K>[]) => void;
  }
) => {
  const data = useOfflineDbData();
  const httpClient = useHttpClient();

  const init = useCallback(async () => {
    const collection =
      await data.resolveCollectionForReplication(collectionName);
    const upsert = callbacks?.upsert;

    const sub = upsert
      ? collection.$.pipe(
          bufferTime(100),
          filter((events) => events.length > 0),
          map((events) => events.flatMap((e) => e.documentData || []))
        ).subscribe(upsert)
      : undefined;

    const replication = setupEntityPush(
      collection as unknown as RxCollection<{
        status?: string;
        syncedAt?: number;
        offlineSyncErrors?: unknown;
      }>,
      httpClient,
      replicationIdentifier
    );

    return () => {
      sub?.unsubscribe();
      // Returned: the next init awaits this cancel settling.
      return replication.cancel();
    };
  }, [
    data,
    collectionName,
    httpClient,
    replicationIdentifier,
    callbacks?.upsert,
  ]);

  return useOfflineProviderInit(init, callbacks?.onSync);
};
